// @flow

import React, { Component } from "react";
import { noop, get, flatten, bindAll, isEqual } from "lodash";
import type { Dispatch } from "redux";
import type { NullaryFn, BinaryFn, UnaryFn } from "@tvg/types/Functional";
import type { RaceTypeCodeEnum } from "@tvg/types/Race";
import formatCurrency from "@tvg/formatter/currency";
import { FeedbackMessageContext } from "@fdr/utils/contextUtils";
import ProcessingOverlay from "@fdr/static-ui/ProcessingOverlay";
import Icon from "@fdr/static-ui/Icons";
import { WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import { trash, cross } from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import { getListRunnerNumberGroup } from "../../_molecules/BetSummary";
import { FloatingBanner, InlineMessage } from "../../_molecules/MessageV2";
import BetAmount from "../../_organisms/BetAmount";
import Button from "../../_atoms/Button";
import Toast from "../../_atoms/Toast";

import {
  BetTicketSection,
  WagersContainer,
  ControlsContainer,
  BetTypeText,
  SelectionsWrapper,
  SelectionsContainer,
  SelectionsEmpty,
  Cancel,
  HelpButtonWrapper,
  InnerHelpButtonWrapper,
  PotReturnWrapper,
  PotReturnSpan,
  BetTypeTrashContainer,
  WagersSubmitContainer,
  FloatingBannerWrapper,
  InlineMessageWrapper,
  BetTicketContainer,
  HelpButton,
  HelpButtonOnboardingTooltip,
  HelpButtonOnboardingTooltipAnchor,
  HelpButtonOnboardingTooltipButton
} from "./styled-components";
import ScrollWrapper from "../../_molecules/ScrollWrapper";

type Props = {
  betType: string,
  selectedBetAmount: number,
  betAmounts: Array<number>,
  minimumWagerAmount: number,
  betTotal: number,
  selections: Array<Array<string>>,
  favoriteRunners: Array<Array<string>>,
  raceTypeCode: RaceTypeCodeEnum,
  isBetDisabled: boolean,
  onPlaceBet: NullaryFn<mixed>,
  onCancelBet: NullaryFn<mixed>,
  onWarningClose?: NullaryFn<mixed>,
  onDismissError?: NullaryFn<mixed>,
  onChangeBetAmount: BinaryFn<number, boolean, mixed>,
  onCloseCustomAmountModal?: NullaryFn<mixed>,
  onVerifyLocation?: NullaryFn<mixed>,
  isKey: boolean,
  isLeg: boolean,
  isMultiLeg: boolean,
  isVisible: boolean,
  isProcessing: boolean,
  isBettingError: boolean,
  isGeolocationErrorShown?: boolean,
  isVerifyLocationEnable?: boolean,
  isSelectionScratched: boolean,
  scratchedSelectionMessage?: string,
  errorBetTitle?: string,
  errorBetMessage?: {
    title: string,
    text: string
  },
  geolocationError?: {
    title: string,
    text: string
  },
  unverifiedUserBetErrorMsg?: {
    title: string,
    text: string
  },
  hasOtherAmount: boolean,
  raceNumber: string,
  modalType: "bottom" | "fade",
  selectTalentIdErrorVisible: boolean,
  dismissTalentPicksWarning: NullaryFn<*>,
  isMobile: boolean,
  inIosAppTablet: boolean,
  numberOfWager: number,
  betLeg: number,
  currentProbable: ?string,
  hasWagerTypeProbable: boolean,
  openPotencialReturnModal: UnaryFn<boolean, void>,
  potentialReturnProgramPage: boolean,
  openPickBetsHowItWorksModal: NullaryFn<mixed>,
  isSpecialGroup: boolean,
  onCloseOnboardingTooltip: NullaryFn<*>,
  onboardingTooltipShow: boolean,
  emptyWPSMessage: string,
  toastCallback?: Function,
  balance: ?number,
  isLogged?: boolean,
  isAccountCompliant?: boolean,
  dispatch: Dispatch<*>,
  openVerifyUserModal?: Function,
  openDepositModal?: Function,
  isBalanceShown?: boolean
};

type State = {
  hasScrollOverflow: boolean
};

/*
 * On a future refactor of BetTicket, consider putting this method inside the Functional component and memoize it's
 *  return, and just run the func when currentProbable changes.
 */
export const hasCalculatedProbable = (
  currentProbable: ?string = "0"
): boolean => currentProbable === "0";

export default class BetTicket extends Component<Props, State> {
  static contextType = FeedbackMessageContext;

  static defaultProps = {
    betType: "WN",
    selectedBetAmount: 0,
    betAmounts: [],
    minimumWagerAmount: 0,
    betTotal: 0,
    selections: [],
    favoriteRunners: [],
    raceTypeCode: "T",
    onPlaceBet: noop,
    onCancelBet: noop,
    onChangeBetAmount: noop,
    onDismissError: noop,
    onVerifyLocation: noop,
    isBetDisabled: false,
    isKey: false,
    isLeg: false,
    isMultiLeg: false,
    isVisible: false,
    isProcessing: false,
    isBettingError: false,
    isGeolocationErrorShown: false,
    isVerifyLocationEnable: false,
    isSelectionScratched: false,
    hasOtherAmount: false,
    raceNumber: "1",
    modalType: "bottom",
    onWarningClose: noop,
    onCloseCustomAmountModal: noop,
    scratchedSelectionMessage: "",
    errorBetTitle: "",
    errorBetMessage: {
      title: "",
      text: ""
    },
    geolocationError: {
      title: "",
      text: ""
    },
    unverifiedUserBetErrorMsg: {
      title: "",
      text: ""
    },
    selectTalentIdErrorVisible: false,
    dismissTalentPicksWarning: noop,
    isMobile: true,
    inIosAppTablet: false,
    numberOfWager: 0,
    betLeg: 0,
    currentProbable: "0",
    hasWagerTypeProbable: false,
    openPotencialReturnModal: noop,
    potentialReturnProgramPage: false,
    openPickBetsHowItWorksModal: noop,
    isSpecialGroup: false,
    onCloseOnboardingTooltip: noop,
    onboardingTooltipShow: false,
    emptyWPSMessage: "",
    toastCallback: noop,
    balance: 0,
    isLogged: false,
    isAccountCompliant: false,
    dispatch: noop,
    openVerifyUserModal: noop,
    openDepositModal: noop
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      hasScrollOverflow: false
    };
    bindAll(this, ["checkScrollOverflow"]);
  }

  shouldComponentUpdate = (nextProps: Props, nextState: State) =>
    !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);

  checkScrollOverflow(status: boolean) {
    this.setState({
      hasScrollOverflow: status
    });
  }

  renderSelections() {
    if (
      flatten(get(this.props, "selections", [])).length === 0 &&
      this.props.isSpecialGroup
    ) {
      return (
        <SelectionsEmpty data-qa-label="betTicket-runnerGroupEmpty">
          {this.props.emptyWPSMessage}
        </SelectionsEmpty>
      );
    }

    const selections = [...this.props.selections];

    if (this.props.betType === WagerTypeCodesEnum.WIN_SHOW) {
      selections[1] = [];
    }

    return getListRunnerNumberGroup(
      [],
      selections,
      this.props.raceTypeCode,
      this.props.isKey,
      this.props.isLeg,
      +this.props.raceNumber,
      this.props.favoriteRunners,
      this.props.betLeg,
      true,
      false,
      this.props.isSpecialGroup,
      this.state.hasScrollOverflow,
      true
    );
  }

  renderButton(
    isInsufficientFundsError: boolean,
    awUserLogged?: boolean,
    betNowButtonLabel: string,
    isAccountCompliant?: boolean
  ) {
    if (
      this.props.isVerifyLocationEnable &&
      this.props.isGeolocationErrorShown
    ) {
      return (
        <Button
          type="betting"
          size="huge"
          isStretched
          onClick={this.props.onVerifyLocation}
          qaLabel="betTicket-verifyLocation"
        >
          Verify Location
        </Button>
      );
    }
    if (isInsufficientFundsError && awUserLogged) {
      return (
        <Button
          type="primary"
          size="huge"
          isStretched
          onClick={this.props.openDepositModal}
          qaLabel="betTicket-depositNow"
        >
          Deposit Now
        </Button>
      );
    }
    if (
      !this.props.isGeolocationErrorShown &&
      this.props.isLogged &&
      !isAccountCompliant
    ) {
      return (
        <Button
          type="betting"
          size="huge"
          isStretched
          onClick={this.props.openVerifyUserModal}
          qaLabel="betTicket-verifyAccount"
        >
          Verify Account
        </Button>
      );
    }

    return (
      <Button
        type="betting"
        size="huge"
        isStretched
        isDisabled={this.props.isBetDisabled}
        onClick={this.props.onPlaceBet}
        qaLabel="betTicket-betNowButton"
        hasRunnersSelected={this.props.betTotal > 0}
      >
        <PotReturnWrapper>
          {this.props.betTotal === 0 ? "Select Runners" : betNowButtonLabel}
          {this.props.potentialReturnProgramPage &&
            !hasCalculatedProbable(this.props.currentProbable) && (
              <PotReturnSpan>
                POT. RETURN {this.props.currentProbable}
              </PotReturnSpan>
            )}
        </PotReturnWrapper>
      </Button>
    );
  }

  render() {
    const toastProps = this.context;
    const betNowButtonLabel = `Bet ${formatCurrency(this.props.betTotal)}`;
    const betErrorMessage = get(this.props, "errorBetMessage");

    const betErrorTitle =
      get(betErrorMessage, "title", "") ||
      `ERROR - ${get(this.props, "errorBetTitle")}`;
    const betErrorText = get(betErrorMessage, "text", "");
    const isInsufficientFundsError =
      typeof this.props.balance === "number" &&
      this.props.balance < this.props.betTotal;

    const awUserLogged =
      this.props.isLogged &&
      this.props.isAccountCompliant &&
      !this.props.isGeolocationErrorShown;

    return (
      <BetTicketContainer>
        <BetTicketSection
          isVisible={this.props.isVisible}
          isMobile={this.props.isMobile}
          hasOnboardingTooltip={this.props.onboardingTooltipShow}
          data-qa-label="betTicket"
          id="bet-ticket-section"
        >
          {awUserLogged &&
            !!this.props.balance &&
            (this.props.isBalanceShown || isInsufficientFundsError) && (
              <InlineMessage
                type={isInsufficientFundsError ? "warning" : "info"}
                text={
                  isInsufficientFundsError
                    ? `Insufficient balance - ${formatCurrency(
                        this.props.balance
                      )}`
                    : `Balance - ${formatCurrency(this.props.balance)}`
                }
                hasIcon={isInsufficientFundsError}
                isInline
                isMessageCenter
              />
            )}
          {toastProps?.showToast && (
            <Toast
              {...toastProps}
              noAnimation={toastProps.isMessageShown}
              callback={this.props.toastCallback}
            />
          )}
          {this.props.isProcessing && (
            <ProcessingOverlay text="Placing bet" smallText />
          )}
          {this.props.isSelectionScratched && (
            <FloatingBannerWrapper>
              <FloatingBanner
                type="warning"
                text={this.props.scratchedSelectionMessage}
                onClose={this.props.onWarningClose}
              />
            </FloatingBannerWrapper>
          )}
          {this.props.isBettingError && (
            <FloatingBannerWrapper>
              <FloatingBanner
                type="error"
                title={betErrorTitle}
                text={betErrorText}
                onClose={this.props.onDismissError}
              />
            </FloatingBannerWrapper>
          )}
          <WagersSubmitContainer>
            {this.props.isGeolocationErrorShown ? (
              <InlineMessageWrapper>
                <InlineMessage
                  type="error"
                  title={get(this.props.geolocationError, "title")}
                  text={get(this.props.geolocationError, "text")}
                />
              </InlineMessageWrapper>
            ) : (
              this.props.isLogged &&
              !this.props.isAccountCompliant && (
                <InlineMessageWrapper>
                  <InlineMessage
                    type="error"
                    title={get(this.props.unverifiedUserBetErrorMsg, "title")}
                    text={get(this.props.unverifiedUserBetErrorMsg, "text")}
                  />
                </InlineMessageWrapper>
              )
            )}
            <WagersContainer hasInlineMessage={awUserLogged}>
              {!this.props.isSpecialGroup && (
                <BetTypeTrashContainer>
                  <BetTypeText data-qa-label="betTicket-betType">
                    {this.props.betType}
                  </BetTypeText>
                </BetTypeTrashContainer>
              )}

              <ScrollWrapper
                isForcingMinWidth={false}
                checkScrollOverflow={this.checkScrollOverflow}
                showFaders={this.state.hasScrollOverflow}
                betType={this.props.betType}
              >
                <SelectionsWrapper
                  key={flatten(this.props.selections).toString()}
                  hasRaceTitle={!this.props.isSpecialGroup}
                  hasScroll={this.state.hasScrollOverflow}
                >
                  <SelectionsContainer>
                    {this.renderSelections()}
                  </SelectionsContainer>
                </SelectionsWrapper>
              </ScrollWrapper>

              <HelpButtonWrapper>
                {this.props.onboardingTooltipShow && (
                  <HelpButtonOnboardingTooltipAnchor>
                    <HelpButtonOnboardingTooltip>
                      Find out more details about pick bets
                      <HelpButtonOnboardingTooltipButton
                        onClick={this.props.onCloseOnboardingTooltip}
                      >
                        <Icon
                          icon={cross}
                          size={16}
                          qaLabel="WarningIcon"
                          color={buildColor("blue", "500")}
                        />
                      </HelpButtonOnboardingTooltipButton>
                    </HelpButtonOnboardingTooltip>
                  </HelpButtonOnboardingTooltipAnchor>
                )}
                <HelpButton
                  onClick={() =>
                    this.props.isMultiLeg
                      ? this.props.openPickBetsHowItWorksModal()
                      : this.props.openPotencialReturnModal(
                          hasCalculatedProbable(this.props.currentProbable)
                        )
                  }
                >
                  {this.props.potentialReturnProgramPage && (
                    <InnerHelpButtonWrapper>
                      {this.props.isMultiLeg ? "Pick Bets" : "Pot. Return"}
                    </InnerHelpButtonWrapper>
                  )}
                </HelpButton>
              </HelpButtonWrapper>
            </WagersContainer>
            <ControlsContainer data-qa-label="betTicket-controls">
              <BetTypeTrashContainer>
                <Cancel
                  size="big"
                  type="secondary"
                  hasRoundedCorners={false}
                  onClick={this.props.onCancelBet}
                  qaLabel="betTicket-cancelButton"
                >
                  <Icon
                    icon={trash}
                    size={16}
                    color={buildColor("red", "500")}
                  />
                </Cancel>
              </BetTypeTrashContainer>
              <BetAmount
                hasOtherAmount={this.props.hasOtherAmount}
                betAmounts={this.props.betAmounts}
                onChangeBetAmount={this.props.onChangeBetAmount}
                selectedBetAmount={this.props.selectedBetAmount}
                onCloseCustomAmountModal={this.props.onCloseCustomAmountModal}
                minimumAmount={this.props.minimumWagerAmount}
                modalType={this.props.modalType}
                inIosAppTablet={this.props.inIosAppTablet}
                numberOfWager={this.props.numberOfWager}
                toastProps={toastProps}
                toastCallback={this.props.toastCallback}
                dispatch={this.props.dispatch}
              />
              {this.renderButton(
                isInsufficientFundsError,
                awUserLogged,
                betNowButtonLabel,
                this.props.isAccountCompliant
              )}
            </ControlsContainer>
          </WagersSubmitContainer>
        </BetTicketSection>
      </BetTicketContainer>
    );
  }
}
